<template>
  <div class="details">
    <div class="details__content" ref="productRef">
      <div class="details__top">
        <h2 class="details__top-title">{{ t('productDetails.title') }}</h2>
        <div class="details__top-block" @click="goBack">
          <img src="@icons/backArrow.svg" alt="arrow">
          <p>{{ t('continueShop') }}</p>
        </div>
      </div>
      <product-details-skeleton v-if="isLoading" />
      <div class="details__main" v-else>
        <div class="details__main-product">
          <img :src="product?.images.edges[0].node.image" :alt="product?.name">
        </div>
        <div class="details__main-info">
          <div>
            <p>{{ product?.name }}</p>
            <p>€ {{ product?.price }}</p>
          </div>
          <div class="details__main-block" v-if="isProductInCart">
            <ui-button class="details__main-button" @click="removeAllProductFormCart">{{ t('buttons.removeProducts') }}</ui-button>
            <div class="details__main-manage">
              <div class="details__main-manage-button" @click="removeProductFormCart">-</div>
              <div class="details__main-manage-count">{{ productQuantity }}</div>
              <div class="details__main-manage-button" @click="addToCart">+</div>
            </div>
          </div>
          <ui-button @click="addToCart" v-else>{{ user ? t('buttons.add') : t('buttons.login') }}</ui-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import {Products} from "@/core/api/products.js";
import {computed, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {onClickOutside} from "@vueuse/core";
import ProductDetailsSkeleton from "@/components/skeletons/product-details-skeleton.vue";
import UiButton from "@/components/ui/ui-button.vue";
import {useAuthStore} from "@/stores/auth.js";
import {useAppStore} from "@/stores/app.js";
import {Orders} from "@/core/api/orders.js";
import {ElNotification} from "element-plus";
import {useCartStore} from "@/stores/cart.js";

const authStore = useAuthStore()
const appStore = useAppStore()
const cartStore = useCartStore()
const {t} = useI18n()
const route = useRoute()
const router = useRouter()

const productRef = ref(null)
const goBack = () => router.go(-1)
onClickOutside(productRef, () => goBack())

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder.uuid ? cartStore.currentOrder.uuid : null
})
const user = computed(() => {
  return authStore.user ? authStore.user : null
})
const productUuid = computed(() => {
  return route.query.productUuid
})

const isLoading = ref(true)
const product = ref(null)

const getProduct = async () => {
  const res = await Products.getProductById(productUuid.value)
  product.value = res.data.products.edges[0].node

  isLoading.value = false
}

const isProductInCart = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === product.value.uuid)
})
const productQuantity = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.length ? cartStore.currentOrder?.orderProducts?.edges.filter((prod) => prod.node.product.uuid === product.value.uuid)[0].node.quantity : 0
})

const addToCart = async () => {
  if (user.value) {
    if (isProductInCart.value) {
      if (product.value.quantity > productQuantity.value) {
        const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, product.value.uuid);
        cartStore.setCurrentOrders(res.data.addOrderProduct.order)

        if (res.errors) {
          ElNotification({
            message: res.errors[0].message,
            type: "error"
          });
        } else {
          ElNotification({
            message: t('popup.addToCart', {product: product.value.name}),
            type: "success"
          });
        }
      } else {
        ElNotification({
          message: t('popup.addToCartLimit', {quantity: product.value.quantity}),
          type: "error"
        });
      }
    } else {
      const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, product.value.uuid);
      cartStore.setCurrentOrders(res.data.addOrderProduct.order)

      if (res.errors) {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      } else {
        ElNotification({
          message: t('popup.addToCart', { product: product.value.name }),
          type: "success"
        });
      }
    }
  } else {
    appStore.setActiveState('signIn')
  }
}

const removeProductFormCart = async () => {
  if (productQuantity.value === 1) {
    await removeAllProductFormCart()
    return
  }

  const res = await Orders.overwriteCart("removeOrderProduct", accessToken.value, orderUuid.value, product.value.uuid);

  cartStore.setCurrentOrders(res.data.removeOrderProduct.order)
}

const removeAllProductFormCart = async () => {
  const res = await Orders.overwriteCart("removeOrderProductsOfAKind", accessToken.value, orderUuid.value, product.value.uuid);
  cartStore.setCurrentOrders(res.data.removeOrderProductsOfAKind.order)
}

onMounted( async () => {
  await getProduct()
})
</script>

<style lang="scss" scoped>
.details {
  position: fixed;
  z-index: 2;
  width: 100vw;
  height: 100vh;
  top: 92px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  background-color: rgba(0, 0, 0, 0.3);

  &__content {
    position: absolute;
    z-index: 2;
    top: 75px;
    width: 50%;
    border-radius: 40px;
    background-color: $white;
    padding: 30px 50px;
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  &__top {
    &-title {
      padding-left: 38px;
      color: #1e1e1e;
      font-family: Poppins, sans-serif;
      font-size: 28px;
      font-weight: 700;
    }

    &-block {
      cursor: pointer;
      display: flex;
      align-items: center;

      & p {
        color: #1e1e1e;
        font-family: Poppins, sans-serif;
        font-size: 28px;
        font-weight: 600;
      }
    }
  }

  &__main {
    width: 100%;
    height: 283px;
    border-top: 1px solid $black;
    padding-top: 20px;
    display: flex;
    align-items: flex-start;
    gap: 50px;
    box-sizing: content-box;

    &-product img {
      width: 225px;
      height: 100%;
      border-radius: 20px;
    }

    &-info {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: space-between;
      width: 100%;
      height: 100%;

      & div {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }

      & p {
        font-family: Poppins, sans-serif;
        font-size: 32px;
        font-weight: 600;
        color: $black;

        &:last-child {
          font-size: 40px;
          font-weight: 700;
          color: $black;
        }
      }
    }

    &-block {
      width: 100%;
      flex-direction: row !important;
      align-items: center !important;
      gap: 25px
    }

    &-manage {
      display: grid !important;
      grid-template-columns: 1fr 2fr 1fr;
      align-items: unset !important;
      border-radius: 14px;
      border: 2px solid $main;
      height: 42px;
      width: 200px;
      flex-shrink: 0;

      & div {
        display: grid;
        place-items: center;

        font-size: 28px;
      }

      &-button {
        cursor: pointer;
        background-color: $main;
        border-radius: 10px 0 0 10px;
        transition: 0.2s;

        color: $white;

        @include hover {
          background-color: $mainHover;
        }

        &:last-child {
          border-radius: 0 10px 10px 0;
        }
      }

      &-count {
        background-color: $white;

        font-size: 22px !important;
      }
    }
  }
}
</style>